import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, CalendarDays, Info } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function statusColor(s) {
  const t = (s || "").toLowerCase();
  if (t.includes("confirm")) return "#00E5A3";
  if (t.includes("expect") || t.includes("probable") || t.includes("projected") || t.includes("likely")) return "#FFB020";
  return "#94A3B8";
}

function likColor(l) {
  if (l === "High") return "#00E5A3";
  if (l === "Medium") return "#FFB020";
  return "#94A3B8";
}

function InjuryTag({ p }) {
  const st = (p.status || "").toLowerCase();
  const c = st.includes("out") || st.includes("susp") ? "#F87171" : st.includes("doubt") ? "#FB923C" : "#FBBF24";
  return (
    <span className="inline-flex items-center gap-1 text-[10px]">
      <span className="text-slate-300">{p.name}</span>
      <span className="font-mono uppercase" style={{ color: c }}>{p.status}</span>
    </span>
  );
}

function GoalieCell({ team, starter, injuries, injuryCount, backup }) {
  const shown = (injuries || []).slice(0, 3);
  const more = (injuryCount || 0) - shown.length;
  return (
    <div className="flex items-start gap-2 min-w-0">
      {team?.logo && <img src={team.logo} alt="" className="h-7 w-7 object-contain shrink-0 mt-0.5" />}
      <div className="min-w-0">
        <div className="text-[11px] font-mono text-slate-500">{team?.abbr}{starter?.role ? ` · ${starter.role}` : ""}</div>
        {starter?.name ? (
          <>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-sm font-semibold text-slate-100 truncate">{starter.name}</span>
              {starter.status && (
                <span data-testid="starter-status" className="text-[9px] font-mono uppercase px-1 py-0.5 rounded"
                  style={{ color: statusColor(starter.status), background: `${statusColor(starter.status)}1a` }}>
                  {starter.status}
                </span>
              )}
            </div>
            {starter.likelihood && (
              <div data-testid="starter-likelihood" className="text-[10px] text-slate-500 mt-0.5">
                Start likelihood:{" "}
                <span className="font-bold" style={{ color: likColor(starter.likelihood) }}>{starter.likelihood}</span>
                <span className="text-slate-600"> · est.</span>
              </div>
            )}
          </>
        ) : <span className="text-xs text-slate-600">TBD</span>}
        {backup?.name && (
          <div data-testid="starter-backup" className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
            <span className="text-slate-600">Backup:</span>
            <span className="text-slate-300">{backup.name}</span>
            {backup.status && backup.status !== "active" && (
              <span className="font-mono uppercase text-[9px]" style={{ color: "#F87171" }}>{backup.status}</span>
            )}
          </div>
        )}
        {injuryCount > 0 && (
          <div data-testid="starter-injuries" className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 max-w-[240px]">
            <span className="text-[10px] font-mono text-slate-600">{injuryCount} injured:</span>
            {shown.map((p) => <InjuryTag key={p.id} p={p} />)}
            {more > 0 && <span className="text-[10px] text-slate-600">+{more}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Goalies() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmedOnly, setConfirmedOnly] = useState(false);
  const [league, setLeague] = useState("NHL");
  useEffect(() => {
    setLoading(true);
    api.get(`/goalies?league=${league}`).then((r) => setGames(r.data.games || [])).catch(() => {}).finally(() => setLoading(false));
  }, [league]);

  const isConf = (s) => (s?.status || "").toLowerCase().includes("confirm");
  const shown = confirmedOnly ? games.filter((g) => isConf(g.away?.starter) || isConf(g.home?.starter)) : games;
  const roleLabel = league === "MLB" ? "Probable Pitchers" : "Starting Goalies";

  return (
    <div data-testid="goalies-page" className="max-w-[940px] mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-100 mb-1 flex items-center gap-2">
        <ShieldAlert size={22} className="text-[#00E5A3]" /> {roleLabel}
        <Badge className="bg-[#00E5A3]/15 text-[#00E5A3] border-0 text-[10px] font-mono">DAILY</Badge>
      </h1>
      <p className="text-sm text-slate-400 mb-3 max-w-2xl">
        {league === "MLB"
          ? "Every MLB game's probable starting pitcher in one glance — the biggest daily pre-game signal in baseball."
          : "Every NHL game's probable and confirmed starting goalie in one glance — the single biggest pre-game swing in hockey. Confirmations firm up ~5 hours before puck drop."}
      </p>
      <div data-testid="likelihood-note" className="flex items-start gap-2 text-[11px] text-slate-500 bg-[#0F1420] border border-[#232D42] rounded-lg px-3 py-2 mb-5 max-w-2xl">
        <Info size={13} className="text-[#38BDF8] mt-0.5 shrink-0" />
        <span>
          <b className="text-slate-300">Status</b> (Confirmed / Expected / Projected) comes straight from ESPN.
          <b className="text-slate-300"> Start likelihood</b> is a SidelineIQ estimate derived only from that real status — not a vendor projection.
          Injured teammates are that team's real, cited absences.{league === "NHL" ? " Backup = the team's other rostered goalie." : ""}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {["NHL", "MLB"].map((lg) => (
          <button key={lg} data-testid={`goalies-league-${lg}`} onClick={() => { setLeague(lg); setConfirmedOnly(false); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${league === lg ? "bg-[#38BDF8] text-[#04140F]" : "bg-[#151B2B] text-slate-300 hover:bg-[#1C2438]"}`}>
            {lg}
          </button>
        ))}
        <span className="w-px h-5 bg-[#232D42] mx-1" />
        <button data-testid="goalies-filter-all" onClick={() => setConfirmedOnly(false)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${!confirmedOnly ? "bg-[#00E5A3] text-[#04140F]" : "bg-[#151B2B] text-slate-300 hover:bg-[#1C2438]"}`}>
          All ({games.length})
        </button>
        <button data-testid="goalies-filter-confirmed" onClick={() => setConfirmedOnly(true)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${confirmedOnly ? "bg-[#00E5A3] text-[#04140F]" : "bg-[#151B2B] text-slate-300 hover:bg-[#1C2438]"}`}>
          Confirmed only
        </button>
      </div>
      {loading ? (
        <div className="text-center py-16 text-slate-500 text-sm">Loading board…</div>
      ) : shown.length === 0 ? (
        <div data-testid="goalies-empty" className="text-center py-16 text-slate-500 text-sm">
          {confirmedOnly
            ? "No confirmed starters yet — still listed as probable. Confirmations firm up a few hours before game time."
            : `No ${league} games scheduled right now. The board fills as the league posts each day's slate.`}
        </div>
      ) : (
        <div className="space-y-2">
          {shown.map((g) => (
            <Link key={g.id} to={`/games/${league}/${g.id}`} data-testid={`goalie-game-${g.id}`}
              className="grid grid-cols-[1fr_auto_1fr] items-start gap-3 rounded-xl bg-[#0F1420] border border-[#232D42] p-3 hover:border-[#00E5A3]/40 transition-colors">
              <GoalieCell team={g.away} starter={g.away?.starter} injuries={g.away?.injuries} injuryCount={g.away?.injury_count} backup={g.away?.backup} />
              <div className="text-center pt-1">
                <div className="text-[10px] font-mono text-slate-500 flex items-center gap-1"><CalendarDays size={10} /> {fmtDate(g.date)}</div>
                <div className="text-[11px] font-mono text-slate-600">@</div>
              </div>
              <div className="flex justify-end">
                <GoalieCell team={g.home} starter={g.home?.starter} injuries={g.home?.injuries} injuryCount={g.home?.injury_count} backup={g.home?.backup} />
              </div>
            </Link>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-600 mt-6">Source: ESPN. Start likelihood is a SidelineIQ estimate, not betting advice.</p>
    </div>
  );
}
